//  watches how wide the right-hand box currently is 
// (it changes when you drag the divider), 
// so PDF pages and Word documents can resize themselves to fit.

import { useEffect, useRef, useState } from 'react';

export interface PaneWidth {
  width: number;         // live — updates every ResizeObserver tick
  settledWidth: number;  // debounced 200ms after resizing stops
}

// One ResizeObserver, two read speeds. Renderers that reflow content on
// width changes (PDF re-rendering canvases at a new scale) should read
// settledWidth so a drag of the split divider doesn't trigger a re-render
// per pixel; renderers that just need current layout width (DOCX's
// scale-transform, grid column sizing) read `width` directly.
export function usePaneWidth<T extends HTMLElement>(): [React.RefObject<T | null>, PaneWidth] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  const [settledWidth, setSettledWidth] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const initial = el.getBoundingClientRect().width;
    setWidth(initial);
    setSettledWidth(initial);

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next === undefined) return;

      setWidth(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => setSettledWidth(next), 200);
    });

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return [ref, { width, settledWidth }];
}
